import { useEffect, useCallback } from 'react';
import { Modal } from 'antd';

/** 避免定时器/焦点触发多次打开同一提示框 */
let versionModalShowing = false;

/**
 * 版本检测 Hook
 * 定期检查服务器上的版本，如果有新版本则提示用户刷新
 * @param {number} checkInterval - 检查间隔时间（毫秒），默认 60 秒
 */
const useVersionCheck = (checkInterval = 60000) => {
    // 获取当前版本（从 index.html 的 hash 或 meta 标签）
    const getCurrentVersion = useCallback(async () => {
        try {
            // 添加时间戳避免缓存
            const response = await fetch(`/index.html?t=${Date.now()}`, {
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });
            const html = await response.text();
            
            // 提取所有 script 和 link 标签的 hash 作为版本标识
            const scriptMatches = html.match(/\/static\/js\/main\.[a-z0-9]+\.js/g) || [];
            const cssMatches = html.match(/\/static\/css\/main\.[a-z0-9]+\.css/g) || [];
            
            // 合并所有 hash 作为版本号
            const version = [...scriptMatches, ...cssMatches].join('|');
            return version;
        } catch (error) {
            console.error('版本检查失败:', error);
            return null;
        }
    }, []);

    // 检查版本更新
    const checkVersion = useCallback(async () => {
        const storedVersion = sessionStorage.getItem('app_version');
        const currentVersion = await getCurrentVersion();
        
        if (!currentVersion) return;
        
        // 首次访问，存储版本号
        if (!storedVersion) {
            sessionStorage.setItem('app_version', currentVersion);
            return;
        }
        
        // 版本不一致，提示更新
        if (storedVersion !== currentVersion) {
            if (versionModalShowing) return;
            versionModalShowing = true;

            Modal.confirm({
                title: '发现新版本',
                content: '检测到系统有更新，请刷新页面以获取最新版本。',
                okText: '立即更新',
                cancelText: '稍后再说',
                centered: true,
                // 返回 Promise：resolve 后 antd 才关弹窗；同步 reload 会打断关闭，表现为要点多次
                onOk: () =>
                    new Promise((resolve) => {
                        sessionStorage.setItem('app_version', currentVersion);
                        window.setTimeout(() => {
                            resolve();
                            window.setTimeout(() => window.location.reload(), 0);
                        }, 0);
                    }),
                onCancel: () => {
                    sessionStorage.setItem('app_version', currentVersion);
                    versionModalShowing = false;
                },
                afterClose: () => {
                    versionModalShowing = false;
                },
            });
        }
    }, [getCurrentVersion]);

    useEffect(() => {
        // 页面加载时检查一次
        checkVersion();
        
        // 定期检查版本
        const intervalId = setInterval(checkVersion, checkInterval);
        
        // 页面可见性变化时检查（用户从其他标签页切换回来时）
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                checkVersion();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // 页面获得焦点时检查
        const handleFocus = () => {
            checkVersion();
        };
        window.addEventListener('focus', handleFocus);
        
        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [checkVersion, checkInterval]);

    return { checkVersion };
};

export default useVersionCheck;
